import moment from 'moment'
import { forwardRef, type HTMLAttributes, type ReactNode, useMemo } from 'react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'
import './WeeklyAppointmentCalendar.css'

const TIME_GUTTER_WIDTH = 80
const HOUR_PX = 88
/** Minimum height for a rendered event block (short slots still clip content cleanly). */
const MIN_EVENT_PX = Math.max(48, Math.round(HOUR_PX * 0.5))
const DAY_COLUMN_MIN_PX = 148
const GRID_MIN_WIDTH_PX = TIME_GUTTER_WIDTH + 7 * DAY_COLUMN_MIN_PX

const DEFAULT_START_HOUR = 0
const DEFAULT_END_HOUR = 24

export type WeeklyAppointmentCalendarEvent = {
  id: string
  start: Date
  end: Date
  doctorName: string
  staffId: string
  /** e.g. Consultation | Physical */
  subtitle: string
  cardStatus: 'success' | 'pending'
  colorVariant: 'violet' | 'amber'
  /** Original API status for client-side filter (table URL parity). */
  rawApiStatus?: string
  /** Optional raw fields for patient URL filters (date / time / type / mode). */
  appointmentAtRaw?: string | null
  startTimeIso?: string | null
  appointmentTypeRaw?: string
  appointmentModeRaw?: string
  purpose?: string
}

export type WeeklyAppointmentCalendarProps = {
  /** First day of the visible week (Sunday). */
  weekStart: Date
  weekRangeLabel: string
  onPrevWeek: () => void
  onNextWeek: () => void
  timeZoneLabel?: string
  events: WeeklyAppointmentCalendarEvent[]
  isLoading?: boolean
  /** Filters / search (right side of toolbar). */
  toolbarRight?: ReactNode
  onEventClick?: (event: WeeklyAppointmentCalendarEvent) => void
  dayStartHour?: number
  dayEndHour?: number
  /** When set, clicking the week range label opens a date picker and this runs with the chosen date (jump to that week). */
  onSelectWeekContainingDate?: (date: Date) => void
  /** Highlighted date in the picker; defaults to first day of the visible week. */
  pickerDate?: Date
}

function initialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '—'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase() || '—'
}

function formatHourLabel(hour: number): string {
  const d = moment().startOf('day').add(hour, 'hours')
  return d.format('h:mm a')
}

function dayIndexInWeek(eventStart: Date, weekStart: Date): number {
  const a = moment(eventStart).startOf('day')
  const b = moment(weekStart).startOf('day')
  const diff = a.diff(b, 'days')
  if (diff < 0 || diff > 6) return -1
  return diff
}

function layoutEvent(
  start: Date,
  end: Date,
  weekStart: Date,
  dayStartHour: number,
  dayEndHour: number,
  totalPx: number
): { top: number; height: number; dayIndex: number } | null {
  const dayIndex = dayIndexInWeek(start, weekStart)
  if (dayIndex < 0) return null

  const startH = moment(start).hour() + moment(start).minute() / 60
  const endH = moment(end).hour() + moment(end).minute() / 60
  const clampedStart = Math.max(startH, dayStartHour)
  const clampedEnd = Math.min(endH, dayEndHour)
  if (clampedEnd <= dayStartHour || clampedStart >= dayEndHour) return null

  let top = (clampedStart - dayStartHour) * HOUR_PX
  top = Math.max(0, Math.min(top, totalPx - MIN_EVENT_PX))
  const rawBottom = (clampedEnd - dayStartHour) * HOUR_PX
  let height = Math.max(MIN_EVENT_PX, rawBottom - top)
  if (top + height > totalPx) height = Math.max(MIN_EVENT_PX, totalPx - top)
  return { dayIndex, height, top }
}

function StatusIcon({ status }: { status: 'success' | 'pending' }) {
  if (status === 'pending') {
    return (
      <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-amber-200/80">
        <svg fill="none" height="11" viewBox="0 0 14 14" width="11" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2.91634 12.8346C2.75107 12.8346 2.61262 12.7786 2.50101 12.6666C2.3894 12.5546 2.3334 12.4162 2.33301 12.2513C2.33262 12.0864 2.38862 11.948 2.50101 11.836C2.6134 11.724 2.75184 11.668 2.91634 11.668H3.49968V9.91797C3.49968 9.32491 3.63832 8.76822 3.91559 8.24789C4.19287 7.72755 4.57923 7.31202 5.07468 7.0013C4.57884 6.69019 4.19248 6.27466 3.91559 5.75472C3.6387 5.23477 3.50007 4.67808 3.49968 4.08464V2.33464H2.91634C2.75107 2.33464 2.61262 2.27864 2.50101 2.16664C2.3894 2.05464 2.3334 1.91619 2.33301 1.7513C2.33262 1.58641 2.38862 1.44797 2.50101 1.33597C2.6134 1.22397 2.75184 1.16797 2.91634 1.16797H11.083C11.2483 1.16797 11.3869 1.22397 11.4989 1.33597C11.6109 1.44797 11.6667 1.58641 11.6663 1.7513C11.666 1.91619 11.61 2.05483 11.4983 2.16722C11.3867 2.27961 11.2483 2.33541 11.083 2.33464H10.4997V4.08464C10.4997 4.67769 10.3612 5.23439 10.0843 5.75472C9.80745 6.27505 9.4209 6.69058 8.92468 7.0013C9.42051 7.31241 9.80707 7.72814 10.0843 8.24847C10.3616 8.7688 10.5001 9.3253 10.4997 9.91797V11.668H11.083C11.2483 11.668 11.3869 11.724 11.4989 11.836C11.6109 11.948 11.6667 12.0864 11.6663 12.2513C11.666 12.4162 11.61 12.5548 11.4983 12.6672C11.3867 12.7796 11.2483 12.8354 11.083 12.8346H2.91634Z"
            fill="#CA8A04"
          />
        </svg>
      </span>
    )
  }
  return (
    <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center">
      <svg fill="none" height="18" viewBox="0 0 18 18" width="18" xmlns="http://www.w3.org/2000/svg">
        <rect fill="white" height="18" rx="9" width="18" />
        <path
          clipRule="evenodd"
          d="M8.99961 15.4016C10.697 15.4016 12.3249 14.7273 13.5251 13.527C14.7253 12.3268 15.3996 10.6989 15.3996 9.00156C15.3996 7.30418 14.7253 5.67631 13.5251 4.47608C12.3249 3.27585 10.697 2.60156 8.99961 2.60156C7.30222 2.60156 5.67436 3.27585 4.47413 4.47608C3.27389 5.67631 2.59961 7.30418 2.59961 9.00156C2.59961 10.6989 3.27389 12.3268 4.47413 13.527C5.67436 14.7273 7.30222 15.4016 8.99961 15.4016ZM11.9652 7.96716C12.1109 7.81628 12.1916 7.6142 12.1897 7.40444C12.1879 7.19468 12.1038 6.99403 11.9555 6.84571C11.8071 6.69738 11.6065 6.61325 11.3967 6.61142C11.187 6.6096 10.9849 6.69024 10.834 6.83596L8.19961 9.47036L7.16521 8.43596C7.01433 8.29024 6.81225 8.2096 6.60249 8.21142C6.39273 8.21325 6.19208 8.29738 6.04375 8.44571C5.89543 8.59403 5.81129 8.79468 5.80947 9.00444C5.80765 9.2142 5.88828 9.41628 6.03401 9.56716L7.63401 11.1672C7.78403 11.3171 7.98748 11.4014 8.19961 11.4014C8.41174 11.4014 8.61519 11.3171 8.76521 11.1672L11.9652 7.96716Z"
          fill="#009A49"
          fillRule="evenodd"
        />
      </svg>
    </span>
  )
}

const WeekRangeLabelButton = forwardRef<HTMLButtonElement, HTMLAttributes<HTMLButtonElement>>(
  ({ children, className = '', ...rest }, ref) => (
    <button
      aria-label="Choose week by date"
      className={`cursor-pointer rounded px-1.5 py-0.5 text-left text-base font-semibold tracking-tight text-[#111827] outline-none hover:bg-gray-100 hover:underline focus-visible:ring-2 focus-visible:ring-gray-300 ${className}`}
      ref={ref}
      type="button"
      {...rest}
    >
      {children}
    </button>
  )
)
WeekRangeLabelButton.displayName = 'WeekRangeLabelButton'

export function WeeklyAppointmentCalendar({
  weekStart,
  weekRangeLabel,
  onPrevWeek,
  onNextWeek,
  timeZoneLabel = 'GMT+01',
  events,
  isLoading,
  toolbarRight,
  onEventClick,
  dayStartHour = DEFAULT_START_HOUR,
  dayEndHour = DEFAULT_END_HOUR,
  onSelectWeekContainingDate,
  pickerDate
}: WeeklyAppointmentCalendarProps) {
  const datePickerSelected = pickerDate ?? weekStart

  const handleDatePickerChange = (d: Date | null) => {
    if (d && onSelectWeekContainingDate) onSelectWeekContainingDate(d)
  }
  const hours = useMemo(
    () => Array.from({ length: dayEndHour - dayStartHour }, (_, i) => dayStartHour + i),
    [dayEndHour, dayStartHour]
  )
  const totalPx = (dayEndHour - dayStartHour) * HOUR_PX

  const dayHeaders = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = moment(weekStart).add(i, 'day')
      return { dateNum: d.format('D'), label: d.format('dddd') }
    })
  }, [weekStart])

  const eventsByDay = useMemo(() => {
    const buckets: WeeklyAppointmentCalendarEvent[][] = [[], [], [], [], [], [], []]
    for (const ev of events) {
      const idx = dayIndexInWeek(ev.start, weekStart)
      if (idx >= 0 && idx <= 6) buckets[idx].push(ev)
    }
    return buckets
  }, [events, weekStart])

  const variantClass: Record<
    WeeklyAppointmentCalendarEvent['colorVariant'],
    { border: string; bg: string; name: string; ring: string }
  > = {
    amber: {
      bg: 'bg-[rgba(202,138,4,0.1)]',
      border: 'border-[#CA8A04]',
      name: 'text-[#CA8A04]',
      ring: 'ring-amber-200/60'
    },
    violet: {
      bg: 'bg-[rgba(124,58,237,0.1)]',
      border: 'border-[#7C3AED]',
      name: 'text-[#7C3AED]',
      ring: 'ring-violet-200/60'
    }
  }

  return (
    <div className="flex flex-1 h-full flex-col overflow-auto rounded-xl border border-[#EDEDED] bg-white shadow-sm">
      {/* Toolbar */}
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-b border-[#EDEDED] px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {onSelectWeekContainingDate ? (
            <ReactDatePicker
              customInput={<WeekRangeLabelButton>{weekRangeLabel}</WeekRangeLabelButton>}
              dateFormat="dd MMM yyyy"
              onChange={handleDatePickerChange}
              popperClassName="weekly-appointment-calendar-datepicker"
              selected={datePickerSelected}
              showMonthDropdown
              showYearDropdown
            />
          ) : (
            <span className="text-base font-semibold tracking-tight text-[#111827]">{weekRangeLabel}</span>
          )}
          <div className="flex items-center gap-1">
            <button
              aria-label="Previous week"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-700 transition-colors hover:bg-gray-50"
              onClick={onPrevWeek}
              type="button"
            >
              <FaChevronLeft size={12} />
            </button>
            <button
              aria-label="Next week"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-700 transition-colors hover:bg-gray-50"
              onClick={onNextWeek}
              type="button"
            >
              <FaChevronRight size={12} />
            </button>
          </div>
        </div>
        {toolbarRight != null ? (
          <div className="flex flex-wrap items-center justify-end gap-2">{toolbarRight}</div>
        ) : null}
      </div>

      {/* Grid */}
      <div className="relative min-h-0 flex-1 overflow-auto">
        <div style={{ minWidth: GRID_MIN_WIDTH_PX }}>
          {/* Column headers */}
          <div className="sticky top-0 z-20 flex border-b border-[#EDEDED] bg-white">
            <div
              className="flex shrink-0 flex-col items-center justify-center border-r border-[#EDEDED] py-2 text-center"
              style={{ width: TIME_GUTTER_WIDTH }}
            >
              <span className="text-[11px] font-medium text-gray-500">{timeZoneLabel}</span>
            </div>
            {dayHeaders.map((h, i) => (
              <div
                className="flex min-w-0 flex-1 flex-col items-center justify-center border-r border-[#EDEDED] py-2 text-center last:border-r-0"
                key={i}
                style={{ minWidth: DAY_COLUMN_MIN_PX }}
              >
                <span className="text-xs text-gray-500">{h.label}</span>
                <span className="text-lg font-semibold text-gray-900">{h.dateNum}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-1 overflow-auto">
            {/* Time gutter */}
            <div className="shrink-0 border-r border-[#EDEDED] bg-white" style={{ width: TIME_GUTTER_WIDTH }}>
              {hours.map(h => (
                <div
                  className="box-border flex items-start justify-end pr-2 pt-0.5 text-[11px] leading-none text-gray-500"
                  key={h}
                  style={{ height: HOUR_PX }}
                >
                  {formatHourLabel(h)}
                </div>
              ))}
            </div>

            {/* Day columns */}
            {eventsByDay.map((dayEvents, dayIndex) => (
              <div
                className="relative min-w-0 flex-1 border-r border-[#EDEDED] last:border-r-0"
                key={dayIndex}
                style={{ minHeight: totalPx, minWidth: DAY_COLUMN_MIN_PX }}
              >
                <div className="pointer-events-none absolute inset-0">
                  {hours.map(h => (
                    <div className="box-border border-b border-[#EDEDED]" key={h} style={{ height: HOUR_PX }} />
                  ))}
                </div>

                {dayEvents
                  .map(ev => {
                    const layout = layoutEvent(ev.start, ev.end, weekStart, dayStartHour, dayEndHour, totalPx)
                    if (!layout || layout.dayIndex !== dayIndex) return null
                    return { ev, layout }
                  })
                  .filter(
                    (
                      row
                    ): row is {
                      ev: WeeklyAppointmentCalendarEvent
                      layout: { top: number; height: number; dayIndex: number }
                    } => row != null
                  )
                  .map(({ ev, layout }) => {
                    const v = variantClass[ev.colorVariant]
                    return (
                      <button
                        className={`absolute left-1.5 right-1.5 z-10 flex min-h-0 flex-col overflow-hidden rounded-md border px-2 py-1.5 text-left shadow-sm ring-1 ${v.bg} ${v.border} ${v.ring} transition hover:brightness-[0.98]`}
                        key={ev.id}
                        // onClick={() => onEventClick?.(ev)}
                        style={{ height: layout.height, top: layout.top }}
                        type="button"
                      >
                        <div className="flex min-h-0 flex-1 gap-2 overflow-hidden">
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${ev.colorVariant === 'violet' ? 'bg-violet-100 text-violet-800' : 'bg-amber-100 text-amber-900'}`}
                          >
                            {initialsFromName(ev.doctorName)}
                          </div>
                          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
                            <div className="flex shrink-0 items-start justify-between gap-1 overflow-hidden">
                              <span
                                className={`min-w-0 truncate text-xs font-semibold leading-tight ${v.name}`}
                                title={ev.doctorName}
                              >
                                {ev.doctorName}
                              </span>
                              <StatusIcon status={ev.cardStatus} />
                            </div>
                            <p
                              className="shrink-0 truncate text-[11px] leading-snug text-gray-600"
                              title={`${moment(ev.start).format('h:mm a')} – ${moment(ev.end).format('h:mm a')}`}
                            >
                              {moment(ev.start).format('h:mm a')} - {moment(ev.end).format('h:mm a')}
                            </p>
                            <p
                              className="line-clamp-2 min-h-0 flex-1 overflow-hidden text-[11px] leading-snug text-gray-600"
                              title={ev.subtitle}
                            >
                              {ev.subtitle}
                            </p>
                          </div>
                        </div>
                      </button>
                    )
                  })}
              </div>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="pointer-events-none absolute inset-0 top-12 flex items-center justify-center bg-white/70 text-sm text-gray-600">
            Loading…
          </div>
        ) : null}
      </div>
    </div>
  )
}
